import libphonenumber from 'google-libphonenumber';
import { validateInvalidCharacters } from './base';

type ValidateReturn = string | null;

/* globals CSS_CONFIG */
declare const CSS_CONFIG: any;

// Safety check: provide fallback if CSS_CONFIG is not defined (e.g., in test environments)
const config = typeof CSS_CONFIG !== 'undefined' ? CSS_CONFIG : {};

const REGEXP = config.REGEXP || {};
const ZIPCODEREGEXP = config.ZIPCODEREGEXP || '';
const EMAILREGEXP = config.EMAILREGEXP || '';
const FORMREQUIRED = config.FORMREQUIRED || {};
const DISALLOWEDREGEXP = config.DISALLOWEDREGEXP || {};

const ZIP_REGEXP = new RegExp(ZIPCODEREGEXP || '');
const EMAIL_REGEXP = new RegExp(EMAILREGEXP || '');
const nameRegExp = new RegExp(REGEXP.name || '');
const streetAddressRegExp = new RegExp(REGEXP.streetAddress || '');
const streetAddress2RegExp = new RegExp(REGEXP.streetAddress2 || '');
const cityRegExp = new RegExp(REGEXP.city || '');
const districtRegExp = new RegExp(REGEXP.district || '');
const blockNumberRegExp = new RegExp(REGEXP.blockNumber || '');

const nameDisallowedRegExp = new RegExp(DISALLOWEDREGEXP.name || '');
const streetAddressDisallowedRegExp = new RegExp(
  DISALLOWEDREGEXP.streetAddress || ''
);
const streetAddress2DisallowedRegExp = new RegExp(
  DISALLOWEDREGEXP.streetAddress2 || ''
);
const cityDisallowedRegExp = new RegExp(DISALLOWEDREGEXP.city || '');
const districtDisallowedRegExp = new RegExp(DISALLOWEDREGEXP.district || '');

export const validateEmail = (value: string): ValidateReturn => {
  return !EMAIL_REGEXP.test(value) ? 'validationBlock.emailInvalid' : null;
};

export const validateName = (
  value: string,
  maxLength: number,
  minLength?: number
): ValidateReturn => {
  const regExp = REGEXP.name ? nameRegExp : undefined;
  const disallowedRegExp = DISALLOWEDREGEXP.name
    ? nameDisallowedRegExp
    : undefined;
  return validateInvalidCharacters(
    value,
    maxLength,
    false,
    regExp,
    disallowedRegExp,
    minLength
  );
};

export const validatestreetAddress = (value: string): ValidateReturn => {
  const regExp = REGEXP.streetAddress ? streetAddressRegExp : undefined;
  const disallowedRegExp = DISALLOWEDREGEXP.streetAddress
    ? streetAddressDisallowedRegExp
    : undefined;
  return validateInvalidCharacters(
    value,
    FORMREQUIRED.maxLimitStreetAddress1,
    false,
    regExp,
    disallowedRegExp
  );
};

export const validatestreetAddress2 = (value: string): ValidateReturn => {
  const regExp = REGEXP.streetAddress2 ? streetAddress2RegExp : undefined;
  const disallowedRegExp = DISALLOWEDREGEXP.streetAddress2
    ? streetAddress2DisallowedRegExp
    : undefined;
  return validateInvalidCharacters(
    value,
    FORMREQUIRED.maxLimitStreetAddress2,
    !FORMREQUIRED.streetAddress2,
    regExp,
    disallowedRegExp
  );
};

export const validatestreetAddress3 = (value: string): ValidateReturn => {
  return validateInvalidCharacters(
    value,
    FORMREQUIRED.maxLimitStreetAddress3 ?? 50,
    true
  );
};

export const validateCity = (value: string): ValidateReturn => {
  const regExp = REGEXP.city ? cityRegExp : undefined;
  const disallowedRegExp = DISALLOWEDREGEXP.city
    ? cityDisallowedRegExp
    : undefined;
  return validateInvalidCharacters(value, 35, false, regExp, disallowedRegExp);
};

export const validateDistrict = (value: string): ValidateReturn => {
  const regExp = REGEXP.district ? districtRegExp : undefined;
  const disallowedRegExp = DISALLOWEDREGEXP.district
    ? districtDisallowedRegExp
    : undefined;
  return validateInvalidCharacters(value, 35, false, regExp, disallowedRegExp);
};

export const validateZip = (value: string): ValidateReturn => {
  return !ZIP_REGEXP.test(value) ? 'validationBlock.postCodeInvalid' : null;
};

export const validateBlockNumber = (value: string): ValidateReturn => {
  return !blockNumberRegExp.test(value)
    ? 'validationBlock.blockNumberInvalid'
    : null;
};

export const checkValidPhoneNumber = (
  phoneNumber: string,
  countryCode: string,
  regex: string
): void => {
  if (!phoneNumber.length) {
    throw new Error('Phone number cannot be empty');
  }
  const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();
  const parsedPhoneNumber = phoneUtil.parseAndKeepRawInput(
    phoneNumber,
    countryCode
  );

  const isValid = regex
    ? new RegExp(regex).test(phoneNumber)
    : phoneUtil.isValidNumber(parsedPhoneNumber);

  if (!isValid) {
    throw new Error('Invalid phone number');
  }
};

export const isValidPhoneNumber = (
  phoneNumber: string,
  countryCode: string,
  regex: string
): boolean => {
  try {
    checkValidPhoneNumber(phoneNumber, countryCode, regex);
    return true;
  } catch {
    return false;
  }
};

export const validatePhoneNumber = (
  value: string,
  countryCode: string
): ValidateReturn => {
  return isValidPhoneNumber(value, countryCode, CSS_CONFIG.MOBILENUMBERREGEXP)
    ? null
    : 'validationBlock.phoneNumberInvalid';
};

export const autoFormatNumber = (
  input: string,
  countryCode: number
): string => {
  const regexLeadingZeros = new RegExp('^[0]+', 'g');
  const regexNonNumbers = new RegExp('[^0-9]', 'g');
  const hasLeadingPlus = input[0] === '+';
  const hasLeadingZeros = input.substr(0, 2) === '00';
  let number = input.replace(regexNonNumbers, '');
  number = number.replace(regexLeadingZeros, '');

  if (hasLeadingZeros || hasLeadingPlus) {
    number = number
      .slice(`${countryCode}`.length)
      .replace(regexLeadingZeros, '');
  }
  if (!number.length) {
    return '';
  }

  return `+${countryCode}${number}`;
};
